import type { StateCreator } from 'zustand';
import { CondoStore } from './shared';
import { apiService } from '../../services/api';

export const createOperationsSlice: StateCreator<CondoStore, [], [], Partial<CondoStore>> = (set, get) => ({
  // ── Work Orders ───────────────────────────────────────────────────────────

  fetchWorkOrders: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const workOrders = await apiService.getWorkOrders(condoId);
      set({ workOrders, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch work orders');
    } finally {
      setLoading(false);
    }
  },

  createWorkOrder: async (woData) => {
    const { setLoading, setError, fetchWorkOrders } = get();
    setLoading(true);
    try {
      await apiService.createWorkOrder(woData);
      await fetchWorkOrders(woData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create work order');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateWorkOrder: async (id, woData) => {
    const { setLoading, setError, workOrders, fetchWorkOrders } = get();
    setLoading(true);
    try {
      await apiService.updateWorkOrder(id, woData);
      const existing = workOrders.find(w => w.id === id);
      await fetchWorkOrders(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update work order');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  completeWorkOrder: async (id, actualCost) => {
    const { setLoading, setError, workOrders, fetchWorkOrders } = get();
    setLoading(true);
    try {
      const existing = workOrders.find(w => w.id === id);
      await apiService.completeWorkOrder(id, actualCost);
      await fetchWorkOrders(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to complete work order');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteWorkOrder: async (id) => {
    const { setLoading, setError, workOrders, fetchWorkOrders } = get();
    setLoading(true);
    try {
      const existing = workOrders.find(w => w.id === id);
      await apiService.deleteWorkOrder(id);
      await fetchWorkOrders(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete work order');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ── Visitor Authorizations ────────────────────────────────────────────────

  fetchVisitorAuthorizations: async (condoId?: string, unitNumber?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const visitorAuthorizations = await apiService.getVisitorAuthorizations(condoId, unitNumber);
      set({ visitorAuthorizations, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch visitor authorizations');
    } finally {
      setLoading(false);
    }
  },

  createVisitorAuthorization: async (vaData) => {
    const { setLoading, setError, fetchVisitorAuthorizations } = get();
    setLoading(true);
    try {
      await apiService.createVisitorAuthorization(vaData);
      await fetchVisitorAuthorizations(vaData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create visitor authorization');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateVisitorAuthorization: async (id, vaData) => {
    const { setLoading, setError, visitorAuthorizations, fetchVisitorAuthorizations } = get();
    setLoading(true);
    try {
      const existing = visitorAuthorizations.find(v => v.id === id);
      await apiService.updateVisitorAuthorization(id, vaData);
      await fetchVisitorAuthorizations(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update visitor authorization');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  revokeVisitorAuthorization: async (id) => {
    const { setLoading, setError, visitorAuthorizations, fetchVisitorAuthorizations } = get();
    setLoading(true);
    try {
      const existing = visitorAuthorizations.find(v => v.id === id);
      await apiService.revokeVisitorAuthorization(id);
      await fetchVisitorAuthorizations(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to revoke visitor authorization');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteVisitorAuthorization: async (id) => {
    const { setLoading, setError, visitorAuthorizations, fetchVisitorAuthorizations } = get();
    setLoading(true);
    try {
      const existing = visitorAuthorizations.find(v => v.id === id);
      await apiService.deleteVisitorAuthorization(id);
      await fetchVisitorAuthorizations(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete visitor authorization');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ── Parking Spots ─────────────────────────────────────────────────────────

  fetchParkingSpots: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const parkingSpots = await apiService.getParkingSpots(condoId);
      set({ parkingSpots, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch parking spots');
    } finally {
      setLoading(false);
    }
  },

  createParkingSpot: async (spotData) => {
    const { setLoading, setError, fetchParkingSpots } = get();
    setLoading(true);
    try {
      await apiService.createParkingSpot(spotData);
      await fetchParkingSpots(spotData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create parking spot');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateParkingSpot: async (id, spotData) => {
    const { setLoading, setError, parkingSpots, fetchParkingSpots } = get();
    setLoading(true);
    try {
      const existing = parkingSpots.find(s => s.id === id);
      await apiService.updateParkingSpot(id, spotData);
      await fetchParkingSpots(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update parking spot');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  assignParkingSpot: async (id, ownerId, ownerName, unitNumber) => {
    const { setLoading, setError, parkingSpots, fetchParkingSpots } = get();
    setLoading(true);
    try {
      const existing = parkingSpots.find(s => s.id === id);
      await apiService.assignParkingSpot(id, ownerId, ownerName, unitNumber);
      await fetchParkingSpots(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to assign parking spot');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  unassignParkingSpot: async (id) => {
    const { setLoading, setError, parkingSpots, fetchParkingSpots } = get();
    setLoading(true);
    try {
      const existing = parkingSpots.find(s => s.id === id);
      await apiService.unassignParkingSpot(id);
      await fetchParkingSpots(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to unassign parking spot');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteParkingSpot: async (id) => {
    const { setLoading, setError, parkingSpots, fetchParkingSpots } = get();
    setLoading(true);
    try {
      const existing = parkingSpots.find(s => s.id === id);
      await apiService.deleteParkingSpot(id);
      await fetchParkingSpots(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete parking spot');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ── Status Certificates ───────────────────────────────────────────────────

  fetchStatusCertificates: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const statusCertificates = await apiService.getStatusCertificates(condoId);
      set({ statusCertificates, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch status certificates');
    } finally {
      setLoading(false);
    }
  },

  prefillStatusCertificate: async (condoId, ownerId) => {
    // Read-only server prefill; errors surface to the caller (the wizard falls
    // back to local computation), so no store error-state mutation here.
    return apiService.prefillStatusCertificate(condoId, ownerId);
  },

  issueStatusCertificate: async (condoId, content, requestId) => {
    const { setLoading, setError, fetchStatusCertificates } = get();
    setLoading(true);
    try {
      const created = await apiService.issueStatusCertificate(condoId, content, requestId);
      await fetchStatusCertificates(condoId);
      return created;
    } catch (error: any) {
      setError(error.message || 'Failed to issue status certificate');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  persistStatusCertificate: async (certData) => {
    const { setLoading, setError, fetchStatusCertificates } = get();
    setLoading(true);
    try {
      await apiService.createStatusCertificate(certData);
      await fetchStatusCertificates(certData.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to save status certificate');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteStatusCertificate: async (id) => {
    const { setLoading, setError, statusCertificates, fetchStatusCertificates } = get();
    setLoading(true);
    try {
      const existing = statusCertificates.find(c => c.id === id);
      await apiService.deleteStatusCertificate(id);
      await fetchStatusCertificates(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete status certificate');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ── Status Certificate Requests ───────────────────────────────────────────

  fetchStatusCertificateRequests: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const statusCertificateRequests = await apiService.getStatusCertificateRequests(condoId!);
      set({ statusCertificateRequests, error: null });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch status certificate requests');
    } finally {
      setLoading(false);
    }
  },

  createStatusCertificateRequest: async (condoId, body) => {
    const { setLoading, setError, fetchStatusCertificateRequests } = get();
    setLoading(true);
    try {
      const created = await apiService.createStatusCertificateRequest(condoId, body);
      await fetchStatusCertificateRequests(condoId);
      return created;
    } catch (error: any) {
      setError(error.message || 'Failed to create status certificate request');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  cancelStatusCertificateRequest: async (id, reason) => {
    const { setLoading, setError, statusCertificateRequests, fetchStatusCertificateRequests } = get();
    setLoading(true);
    try {
      const existing = statusCertificateRequests.find(r => r.id === id);
      await apiService.cancelStatusCertificateRequest(id, reason);
      await fetchStatusCertificateRequests(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to cancel status certificate request');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ── Inspections ───────────────────────────────────────────────────────────

  fetchInspections: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const inspections = await apiService.getInspections(condoId);
      set({ inspections });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch inspections');
    } finally {
      setLoading(false);
    }
  },

  createInspection: async (data) => {
    const { setLoading, setError, fetchInspections } = get();
    setLoading(true);
    try {
      await apiService.createInspection(data);
      await fetchInspections(data.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create inspection');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updateInspection: async (id, data) => {
    const { setLoading, setError, inspections, fetchInspections } = get();
    setLoading(true);
    try {
      await apiService.updateInspection(id, data);
      const existing = inspections.find(i => i.id === id);
      await fetchInspections(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update inspection');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  completeInspection: async (id, result) => {
    const { setLoading, setError, inspections, fetchInspections } = get();
    setLoading(true);
    try {
      await apiService.completeInspection(id, result);
      const existing = inspections.find(i => i.id === id);
      await fetchInspections(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to complete inspection');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  addInspectionNote: async (id, note) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.addInspectionNote(id, note);
      set(state => ({ inspections: state.inspections.map(i => i.id === id ? updated : i) }));
    } catch (error: any) {
      setError(error.message || 'Failed to add inspection note');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deleteInspection: async (id) => {
    const { setLoading, setError, inspections, fetchInspections } = get();
    setLoading(true);
    try {
      const existing = inspections.find(i => i.id === id);
      await apiService.deleteInspection(id);
      await fetchInspections(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete inspection');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  // ── Packages ──────────────────────────────────────────────────────────────

  fetchPackages: async (condoId?: string) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const packages = await apiService.getPackages(condoId);
      set({ packages });
    } catch (error: any) {
      setError(error.message || 'Failed to fetch packages');
    } finally {
      setLoading(false);
    }
  },

  createPackage: async (data) => {
    const { setLoading, setError, fetchPackages } = get();
    setLoading(true);
    try {
      await apiService.createPackage(data);
      await fetchPackages(data.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to create package');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  updatePackage: async (id, data) => {
    const { setLoading, setError, packages, fetchPackages } = get();
    setLoading(true);
    try {
      await apiService.updatePackage(id, data);
      const existing = packages.find(p => p.id === id);
      await fetchPackages(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to update package');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  notifyPackageResident: async (id) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.notifyPackageResident(id);
      set(state => ({ packages: state.packages.map(p => p.id === id ? updated : p) }));
    } catch (error: any) {
      setError(error.message || 'Failed to notify resident');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  markPackagePickedUp: async (id) => {
    const { setLoading, setError } = get();
    setLoading(true);
    try {
      const updated = await apiService.markPackagePickedUp(id);
      set(state => ({ packages: state.packages.map(p => p.id === id ? updated : p) }));
    } catch (error: any) {
      setError(error.message || 'Failed to mark package as picked up');
      throw error;
    } finally {
      setLoading(false);
    }
  },

  deletePackage: async (id) => {
    const { setLoading, setError, packages, fetchPackages } = get();
    setLoading(true);
    try {
      const existing = packages.find(p => p.id === id);
      await apiService.deletePackage(id);
      await fetchPackages(existing?.condoId);
    } catch (error: any) {
      setError(error.message || 'Failed to delete package');
      throw error;
    } finally {
      setLoading(false);
    }
  },
});
